import { createSelector } from 'reselect';
// Import state types.
import {
	AppState,
	PolicyState,
	PublicPolicyViewState,
} from '@app/redux/states';
// Import props.
import {
	MortgageeProps,
	OptionalMortgageeProps,
	PolicyProps,
} from '@app/props';
// Import api models.
import {
	BuildingResponseCommercialExistingWithStatus,
	BuildingResponsePersonalWithStatus,
	ComparisonSummaryEntity,
	ComparisonSummaryMortgagees,
	InsuredInfo,
	Mortgagee,
	MortgageeRequest,
	MortgageeWithStatus,
	PolicyCost,
	PolicyTermWithStatus,
	ProducerInfo,
	PublicPolicyViewData,
	PublicPolicyViewSection,
} from '@app/models/api';
// Import base selectors.
import {
	getPoliciesState,
} from '@app/redux/selectors/base';
// Import selectors.
import {
	makeGetPublicPolicyViewMortgageeSectionsSelector,
	makeGetPublicPolicyViewLandingSectionSelector,
} from '@app/redux/selectors';
// Import enumerations.
import {
	BroadLineOfBusiness,
	Peril,
	PolicyTermStatus,
	SectionStatus,
} from '@app/enumerations';
// Import services.
import {
	AddressService,
} from '@app/services';

// Fetches the public policy view state.
const getPublicPolicyViewState = (state: AppState, props: PolicyProps): PublicPolicyViewState | undefined => {
	let policyState: PolicyState | undefined = getPoliciesState(state, props);
	return policyState?.publicPolicyView ?? undefined;
}

// Get Public Policy View Data Selector.
const makeGetPublicPolicyViewDataSelector = () => createSelector(
	[getPublicPolicyViewState],
	(publicPolicyViewState: PublicPolicyViewState | undefined): PublicPolicyViewData | undefined => {
		return publicPolicyViewState?.publicPolicyViewData ?? undefined;
	}
);

// Get Public Policy View Data Broad Line Of Business Selector.
const makeGetPublicPolicyViewDataBroadLineOfBusinessSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): BroadLineOfBusiness | undefined => {
		return publicPolicyViewData?.broadLineOfBusiness?.code ?? undefined;
	}
);

// Get Public Policy View Policy Name Prefix Selector.
const makeGetPublicPolicyViewPolicyNamePrefixSelector = () => createSelector(
	[makeGetPublicPolicyViewDataBroadLineOfBusinessSelector()],
	(broadLineOfBusiness: BroadLineOfBusiness | undefined): string | undefined => {
		let policyNamePrefix: string | undefined = undefined;
		if (broadLineOfBusiness !== undefined) {
			policyNamePrefix = broadLineOfBusiness === BroadLineOfBusiness.PERSONAL
				? 'NCIP'
				: 'CNCIP';
		}
		return policyNamePrefix;
	}
);

// Get Public Policy View Policy Name Selector.
const makeGetPublicPolicyViewPolicyNameSelector = () => createSelector(
	[
		(state: AppState, props: PolicyProps) => props.policyId,
		makeGetPublicPolicyViewPolicyNamePrefixSelector(),
	],
	(policyId: number, policyNamePrefix: string | undefined): string => {
		return [policyNamePrefix, policyId.toString()]
			.filter((token) => !String.isNullOrWhiteSpace(token))
			.join('');
	}
);

// Get Public Policy View Building Personal Selector.
const makeGetPublicPolicyViewBuildingPersonalSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): BuildingResponsePersonalWithStatus | undefined => {
		return publicPolicyViewData?.risk ?? undefined;
	}
);

// Get Public Policy View Buildings Commercial Selector.
const makeGetPublicPolicyViewBuildingsCommercialSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): BuildingResponseCommercialExistingWithStatus[] => {
		return publicPolicyViewData?.risks ?? [];
	}
);

// Get Public Policy View Peril Codes Selector.
const makeGetPublicPolicyViewPerilCodesSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): Peril[] => {
		return publicPolicyViewData?.perilsDeductibles
			.map((perilDeductible) => perilDeductible.perilCode ?? Peril.NONE)
			.filter((perilCode) => perilCode !== Peril.NONE) ?? [];
	}
);

// Get Public Policy View Covered Perils Selector.
const makeGetPublicPolicyViewCoveredPerilsSelector = () => createSelector(
	[makeGetPublicPolicyViewPerilCodesSelector()],
	(perilCodes: Peril[]): string[] => {
		return perilCodes.map((perilCode) => Peril.getPerilName(perilCode));
	}
);

// Get Public Policy View Insured Info Selector.
const makeGetPublicPolicyViewInsuredInfoSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): InsuredInfo | undefined => {
		return publicPolicyViewData?.namedInsured ?? undefined;
	}
);

// Get Public Policy View Producer Info Selector.
const makeGetPublicPolicyViewProducerInfoSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): ProducerInfo | undefined => {
		return publicPolicyViewData?.producer ?? undefined;
	}
);

// Get Public Policy Terms With Status Selector.
const makeGetPublicPolicyTermsWithStatusSelector = () => createSelector(
	[getPublicPolicyViewState],
	(publicPolicyViewState: PublicPolicyViewState | undefined): PolicyTermWithStatus[] => {
		return publicPolicyViewState?.publicPolicyTermsWithStatus ?? [];
	}
);

// Get Public Policy View Mortgagees Selector.
const makeGetPublicPolicyViewMortgageesSelector = () => createSelector(
	[makeGetPublicPolicyViewDataSelector()],
	(publicPolicyViewData: PublicPolicyViewData | undefined): MortgageeWithStatus[] => {
		return publicPolicyViewData?.mortgagees ?? [];
	}
);

// Get Public Policy View Mortgagee Guids Selector.
const makeGetPublicPolicyViewMortgageeGuidsSelector = () => createSelector(
	[makeGetPublicPolicyViewMortgageesSelector()],
	(mortgagees: MortgageeWithStatus[]): string[] =>
		mortgagees
			.map<string>((mortgagee: MortgageeWithStatus) => mortgagee.id ?? String.empty())
			.filter((mortgageeGuid: string) => !String.isNullOrWhiteSpace(mortgageeGuid))
);

// Get Public Policy View Mortgagee Emails In Use Selector.
const makeGetPublicPolicyViewMortgageeEmailsInUseSelector = () => createSelector(
	[
		(state: AppState, props: OptionalMortgageeProps) => props.mortgageeGuid,
		makeGetPublicPolicyViewMortgageesSelector()
	],
	(
		mortgageeGuidToExclude: string | undefined, 
		mortgagees: MortgageeWithStatus[]
	): string[] =>
		mortgagees
			.filter((mortgagee: MortgageeWithStatus) => mortgageeGuidToExclude === undefined || mortgagee.id !== mortgageeGuidToExclude)
			.map<string>((mortgagee: MortgageeWithStatus) => mortgagee.email ?? String.empty())
			.filter((emailInUse: string) => !String.isNullOrWhiteSpace(emailInUse))
);

// Get Public Policy View Mortgagee Lienholder Positions In Use Selector.
const makeGetPublicPolicyViewMortgageeLienholderPositionsInUseSelector = () => createSelector(
	[
		(state: AppState, props: OptionalMortgageeProps) => props.mortgageeGuid,
		makeGetPublicPolicyViewMortgageesSelector()
	],
	(
		mortgageeGuidToExclude: string | undefined, 
		mortgagees: MortgageeWithStatus[]
	): number[] =>
		mortgagees
			.filter((mortgagee: MortgageeWithStatus) => mortgageeGuidToExclude === undefined || mortgagee.id !== mortgageeGuidToExclude)
			.map<number>((mortgagee: MortgageeWithStatus) => mortgagee.lienPositionNumber ?? -1)
			.filter((lienPositionNumberInUse: number) => lienPositionNumberInUse !== -1)
);

// Get Public Policy View Mortgagee Selector.
const makeGetPublicPolicyViewMortgageeSelector = () => createSelector(
	[
		(state: AppState, props: MortgageeProps) => props.mortgageeGuid,
		makeGetPublicPolicyViewMortgageesSelector()
	],
	(
		mortgageeGuid: string,
		mortgagees: MortgageeWithStatus[],
	): MortgageeWithStatus | undefined =>
		mortgagees.find((mortgagee: MortgageeWithStatus) => mortgagee.id === mortgageeGuid)
);

// Get Public Policy View Mortgagee As Request Selector.
const makeGetPublicPolicyViewMortgageeAsRequestSelector = () => createSelector(
	[makeGetPublicPolicyViewMortgageeSelector()],
	(mortgagee: MortgageeWithStatus | undefined): MortgageeRequest | undefined => {
		let mortgageeRequest: MortgageeRequest | undefined = undefined;
		if (mortgagee !== undefined) {
			mortgageeRequest = new MortgageeRequest();
			mortgageeRequest.name = mortgagee.name;
			mortgageeRequest.includeSuccessorOrAssigns = mortgagee.includeItsSuccessorsAndOrAssigns;
			mortgageeRequest.includeInterests = mortgagee.includeAsTheirInterestsMayAppear;
			mortgageeRequest.loanNumber = mortgagee.loanNumber;
			mortgageeRequest.positionNumber = mortgagee.lienPositionNumber;
			mortgageeRequest.isMortgageeBilled = mortgagee.billMortgagee;
			mortgageeRequest.isMortgageeRequired = mortgagee.coverageRequiredByMortgagee;
			mortgageeRequest.address = mortgagee.mailingAddress;
			mortgageeRequest.email = mortgagee.email;
			mortgageeRequest.sendPaymentReceiptToMortgagee = mortgagee.sendPaymentReceiptToEmail;
		}
		return mortgageeRequest;
	}
);

// Get Public Policy Most Recent Past Term Year Selector.
const makeGetPublicPolicyMostRecentPastTermYearSelector = () => createSelector(
	[makeGetPublicPolicyTermsWithStatusSelector()],
	(policyTermsWithStatus: PolicyTermWithStatus[]): number | undefined => {
		let mostRecentPastTermYear: number = Math.max(...policyTermsWithStatus
			.filter((policyTermWithStatus) => policyTermWithStatus.termStatus === PolicyTermStatus.PAST)
			.map((policyTermWithStatus) => policyTermWithStatus.termYear ?? 0)
			.filter((termYear) => termYear !== 0));
		return mostRecentPastTermYear > 0 ? mostRecentPastTermYear : undefined;
	}
);


// Get Public Policy Term Year In Force Selector.
const makeGetPublicPolicyTermYearInForceSelector = () => createSelector(
	[
		makeGetPublicPolicyTermsWithStatusSelector(),
		makeGetPublicPolicyMostRecentPastTermYearSelector(),
	],
	(
		policyTermsWithStatus: PolicyTermWithStatus[],
		mostRecentPastTermYear: number | undefined,
	): number | undefined => {
		let result = policyTermsWithStatus.find((policyTermWithStatus) => policyTermWithStatus.termStatus === PolicyTermStatus.IN_EFFECT)?.termYear
			?? policyTermsWithStatus.find((policyTermWithStatus) => policyTermWithStatus.termStatus === PolicyTermStatus.CANCELED)?.termYear
			?? mostRecentPastTermYear
			?? undefined;
		console.log(result);
		return result;
	}
);

// Get Public Policy Current Term Selector.
const makeGetPublicPolicyCurrentTermSelector = () => createSelector(
	[
		makeGetPublicPolicyTermYearInForceSelector(),
		makeGetPublicPolicyTermsWithStatusSelector(),
	],
	(
		policyTermYearInForce: number | undefined,
		policyTermsWithStatus: PolicyTermWithStatus[]
	): PolicyTermWithStatus | undefined => {
		return policyTermYearInForce !== undefined
			? policyTermsWithStatus.find((policyTermWithStatus) => policyTermWithStatus.termYear === policyTermYearInForce)
			: undefined;
	}
);

// Get Public Policy Current Term Cost Selector.
const makeGetPublicPolicyCurrentTermCostSelector = () => createSelector(
	[makeGetPublicPolicyCurrentTermSelector()],
	(policyCurrentTerm: PolicyTermWithStatus | undefined): PolicyCost | undefined => {
		return policyCurrentTerm?.cost ?? undefined;
	}
);

// Get Public Policy Current Term Effective Date Selector.
const makeGetPublicPolicyCurrentTermEffectiveDateSelector = () => createSelector(
	[makeGetPublicPolicyCurrentTermSelector()],
	(policyCurrentTerm: PolicyTermWithStatus | undefined): Date | undefined => {
		return policyCurrentTerm?.effectiveDate ?? undefined;
	}
);

// Get Public Policy Current Term End Date Selector.
const makeGetPublicPolicyCurrentTermEndDateSelector = () => createSelector(
	[makeGetPublicPolicyCurrentTermSelector()],
	(policyCurrentTerm: PolicyTermWithStatus | undefined): Date | undefined => {
		return policyCurrentTerm?.endDate ?? undefined;
	}
);

// Get Public Policy Next Term Selector.
const makeGetPublicPolicyNextTermSelector = () => createSelector(
	[makeGetPublicPolicyTermsWithStatusSelector()],
	(policyTermsWithStatus: PolicyTermWithStatus[]): PolicyTermWithStatus | undefined => {
		return policyTermsWithStatus.find((policyTermWithStatus) => policyTermWithStatus.termStatus === PolicyTermStatus.PAID_PENDING_EFFECTIVE_DATE || policyTermWithStatus.termStatus === PolicyTermStatus.PENDING_PAYMENT) ?? undefined;
	}
);

// Get Public Policy Next Term Cost Selector.
const makeGetPublicPolicyNextTermCostSelector = () => createSelector(
	[makeGetPublicPolicyNextTermSelector()],
	(policyNextTerm: PolicyTermWithStatus | undefined): PolicyCost | undefined => {
		return policyNextTerm?.cost ?? undefined;
	}
);

// Get Public Policy Next Term Effective Date Selector.
const makeGetPublicPolicyNextTermEffectiveDateSelector = () => createSelector(
	[makeGetPublicPolicyNextTermSelector()],
	(policyNextTerm: PolicyTermWithStatus | undefined): Date | undefined => {
		return policyNextTerm?.effectiveDate ?? undefined;
	}
);

// Get Public Policy Next Term End Date Selector.
const makeGetPublicPolicyNextTermEndDateSelector = () => createSelector(
	[makeGetPublicPolicyNextTermSelector()],
	(policyNextTerm: PolicyTermWithStatus | undefined): Date | undefined => {
		return policyNextTerm?.endDate ?? undefined;
	}
);

// Is Public Policy View Data Loaded Selector.
const makeIsPublicPolicyViewDataLoadedSelector = () => createSelector(
	[getPublicPolicyViewState],
	(publicPolicyViewState: PublicPolicyViewState | undefined): boolean => {
		return publicPolicyViewState?.isPublicPolicyViewDataLoaded ?? false;
	}
);

// Are Public Policy Terms With Status Loaded Selector.
const makeArePublicPolicyTermsWithStatusLoadedSelector = () => createSelector(
	[getPublicPolicyViewState],
	(publicPolicyViewState: PublicPolicyViewState | undefined): boolean => {
		return publicPolicyViewState?.arePublicPolicyTermsWithStatusLoaded ?? false;
	}
);

// Is Public Policy Active For Changes Selector.
const makeIsPublicPolicyActiveForChangesSelector = () => createSelector(
	[makeGetPublicPolicyTermsWithStatusSelector()],
	(policyTermsWithStatus: PolicyTermWithStatus[]): boolean => {
		return policyTermsWithStatus.length > 0 && !policyTermsWithStatus.some((policyTermWithStatus) => policyTermWithStatus.termStatus === PolicyTermStatus.CANCELED);
	}
);

// Is Public Policy Payment Due Selector.
const makeIsPublicPolicyPaymentDueSelector = () => createSelector(
	[makeGetPublicPolicyTermsWithStatusSelector()],
	(policyTermsWithStatus: PolicyTermWithStatus[]): boolean => {
		return policyTermsWithStatus.some((policyTermWithStatus) => policyTermWithStatus.termStatus === PolicyTermStatus.PENDING_PAYMENT);
	}
);

// #region Comparison Selectors.

// Get Public Policy View Comparison Mortgagee Summaries Selector.
const makeGetPublicPolicyViewComparisonMortgageeSummariesSelector = () => createSelector(
	[
		makeGetPublicPolicyViewMortgageesSelector(),
		makeGetPublicPolicyViewMortgageeSectionsSelector(),
	],
	(
		mortgagees: MortgageeWithStatus[],
		mortgageeSections: PublicPolicyViewSection[],
	): ComparisonSummaryEntity<Mortgagee>[] => {
		return mortgagees.map((mortgagee: MortgageeWithStatus) => {
			const matchingMortgageeSection: PublicPolicyViewSection | undefined = mortgageeSections.find((mortgageeSection: PublicPolicyViewSection) => {
				return mortgagee.id !== null
					&& mortgageeSection.key?.toLowerCase() === mortgagee.id.toLowerCase();
			});
			const isSectionEditable: boolean = matchingMortgageeSection?.isEditable ?? false;
			const requiresReview: boolean = matchingMortgageeSection?.status?.statusCode === SectionStatus.REQUIRED;
			const singleLineAddress: string | undefined = mortgagee.mailingAddress !== null ? AddressService.getInstance().formatAddressToSingleLineString(mortgagee.mailingAddress) : undefined
			let copiedMortgagee: MortgageeWithStatus = {
				...mortgagee,
				mailingAddress: null,
			};

			return new ComparisonSummaryEntity<Mortgagee>({
				id: mortgagee.id ?? String.empty(),
				descriptor: `${mortgagee.name}`,
				subDescriptor: singleLineAddress,
				requiresReview: requiresReview,
				allowEdits: isSectionEditable,
				allowRemoval: isSectionEditable,
				oldData: copiedMortgagee,
				newData: copiedMortgagee,
			});
		});
	},
);

// Get Public Policy View Comparison Mortgagee Summaries Ordered Selector.
const makeGetPublicPolicyViewComparisonMortgageeSummariesOrderedSelector = () => createSelector(
	[makeGetPublicPolicyViewComparisonMortgageeSummariesSelector()],
	(comparisonMortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[]): ComparisonSummaryEntity<Mortgagee>[] => {
		return comparisonMortgageeSummaries.sort((comparisonMortgageeSummaryDataA: ComparisonSummaryEntity<Mortgagee>, comparisonMortgageeSummaryDataB: ComparisonSummaryEntity<Mortgagee>) => {
			if (comparisonMortgageeSummaryDataA.descriptor > comparisonMortgageeSummaryDataB.descriptor) {
				return 1;
			}
			else if (comparisonMortgageeSummaryDataA.descriptor < comparisonMortgageeSummaryDataB.descriptor) {
				return -1;
			}
			return 0;
		});
	},
);

// Get Public Policy View Comparison Mortgagees Summary Selector.
const makeGetPublicPolicyViewComparisonMortgageesSummarySelector = () => createSelector(
	[
		makeGetPublicPolicyViewComparisonMortgageeSummariesOrderedSelector(),
		makeGetPublicPolicyViewLandingSectionSelector(),
	],
	(
		comparisonMortgageeSummaries: ComparisonSummaryEntity<Mortgagee>[],
		publicPolicyViewLandingSection: PublicPolicyViewSection,
	): ComparisonSummaryMortgagees => {
		return new ComparisonSummaryMortgagees({
			allowAdds: publicPolicyViewLandingSection.isEditable ?? false,
			comparisonEntitySummaries: comparisonMortgageeSummaries,
		});
	},
);

// #endregion Comparison Selectors.

// Export composed methods.
export {
	getPublicPolicyViewState,
};

// export selectors.
export {
	makeGetPublicPolicyViewDataSelector,
	makeGetPublicPolicyViewDataBroadLineOfBusinessSelector,
	makeGetPublicPolicyViewPolicyNamePrefixSelector,
	makeGetPublicPolicyViewPolicyNameSelector,
	makeGetPublicPolicyViewBuildingPersonalSelector,
	makeGetPublicPolicyViewBuildingsCommercialSelector,
	makeGetPublicPolicyViewCoveredPerilsSelector,
	makeGetPublicPolicyViewInsuredInfoSelector,
	makeGetPublicPolicyViewProducerInfoSelector,
	makeGetPublicPolicyViewMortgageesSelector,
	makeGetPublicPolicyViewMortgageeGuidsSelector,
	makeGetPublicPolicyViewMortgageeEmailsInUseSelector,
	makeGetPublicPolicyViewMortgageeLienholderPositionsInUseSelector,
	makeGetPublicPolicyViewMortgageeSelector,
	makeGetPublicPolicyViewMortgageeAsRequestSelector,
	makeGetPublicPolicyCurrentTermSelector,
	makeGetPublicPolicyCurrentTermCostSelector,
	makeGetPublicPolicyCurrentTermEffectiveDateSelector,
	makeGetPublicPolicyCurrentTermEndDateSelector,
	makeGetPublicPolicyNextTermSelector,
	makeGetPublicPolicyNextTermCostSelector,
	makeGetPublicPolicyNextTermEffectiveDateSelector,
	makeGetPublicPolicyNextTermEndDateSelector,
	makeGetPublicPolicyMostRecentPastTermYearSelector,
	makeGetPublicPolicyTermYearInForceSelector,
	makeGetPublicPolicyTermsWithStatusSelector,
	makeIsPublicPolicyViewDataLoadedSelector,
	makeArePublicPolicyTermsWithStatusLoadedSelector,
	makeIsPublicPolicyActiveForChangesSelector,
	makeIsPublicPolicyPaymentDueSelector,
	// Comparison Selectors.
	makeGetPublicPolicyViewComparisonMortgageeSummariesSelector,
	makeGetPublicPolicyViewComparisonMortgageeSummariesOrderedSelector,
	makeGetPublicPolicyViewComparisonMortgageesSummarySelector,
};